import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  Link,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import awsRegionsList from "aws-regions/regions.json";
import {
  CloudProviderContext,
  getAccountCloudType,
} from "../../../CloudProviderContext";
import {
  useAccounts,
  useOrganization,
  useUsers,
} from "../../../components/Common/HelperComponents/GetDataFromStore";
import CloudProviderMark from "../../../components/Common/CloudProviderMark";
import { getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";
import { listAllCatalogFrameworks } from "../../../helpers/auditHub_helper";
import {
  AWS_ACCOUNT_NUMBER,
  CREATE_DRAFT_STORAGE_KEY,
  MAX_ACCOUNT_FRAMEWORKS,
  MAX_ACCOUNT_REGIONS,
  MAX_AUDIT_ACCOUNTS,
} from "./constants";
import DueDateField from "./DueDateField";
import { excludeMspUsers, formatUserLabel, frameworkLabel } from "./utils";
import {
  createAudit,
  fetchAudits,
  resetCreateAudit,
} from "../../../store/auditHub/auditHubSlice";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  AuditHubThemeProvider,
  auditHubPageSx,
  cardShellSx,
  outlinedButtonSx,
  primaryButtonSx,
  searchFieldSx,
  tableShellSx,
} from "./theme";

const asInteger = (value) => {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (typeof value === "string" && /^-?\d+$/.test(value.trim())) return Number(value.trim());
  return null;
};

const AWS_REGION_CODES = (Array.isArray(awsRegionsList) ? awsRegionsList : [])
  .map((region) => region?.code)
  .filter((code) => typeof code === "string" && code);

const emptyAccount = (accountNumber, regions = []) => ({
  accountNumber,
  regions,
  complianceAddonIds: [],
});

const emptyForm = () => ({
  name: "",
  ownerEmail: "",
  defaultAssigneeEmail: "",
  dueDate: "",
  accounts: [],
});

const normalizeAccounts = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry) => entry && typeof entry.accountNumber === "string")
    .slice(0, MAX_AUDIT_ACCOUNTS)
    .map((entry) => ({
      accountNumber: entry.accountNumber,
      regions: Array.isArray(entry.regions)
        ? entry.regions.filter((region) => typeof region === "string" && region.trim()).slice(0, MAX_ACCOUNT_REGIONS)
        : [],
      complianceAddonIds: Array.isArray(entry.complianceAddonIds)
        ? entry.complianceAddonIds.map(asInteger).filter((id) => id != null).slice(0, MAX_ACCOUNT_FRAMEWORKS)
        : [],
    }));
};

const readDraft = () => {
  try {
    const raw = sessionStorage.getItem(CREATE_DRAFT_STORAGE_KEY);
    if (!raw) return emptyForm();
    const parsed = JSON.parse(raw);
    const accounts = normalizeAccounts(parsed.accounts);
    if (!accounts.length && parsed.accountNumber) {
      const addonId = asInteger(parsed.complianceAddonId);
      accounts.push({
        ...emptyAccount(parsed.accountNumber),
        complianceAddonIds: addonId == null ? [] : [addonId],
      });
    }
    return {
      ...emptyForm(),
      name: typeof parsed.name === "string" ? parsed.name : "",
      ownerEmail: typeof parsed.ownerEmail === "string" ? parsed.ownerEmail : "",
      defaultAssigneeEmail: typeof parsed.defaultAssigneeEmail === "string" ? parsed.defaultAssigneeEmail : "",
      dueDate: typeof parsed.dueDate === "string" ? parsed.dueDate : "",
      accounts,
    };
  } catch (_e) {
    return emptyForm();
  }
};

const writeDraft = (form) => {
  try {
    sessionStorage.setItem(CREATE_DRAFT_STORAGE_KEY, JSON.stringify(form));
  } catch (_e) {
    /* ignore */
  }
};

const clearDraft = () => {
  try {
    sessionStorage.removeItem(CREATE_DRAFT_STORAGE_KEY);
  } catch (_e) {
    /* ignore */
  }
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
};

const clampSx = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
});

const SectionCard = ({ title, subtitle, action, children, sx }) => (
  <Box sx={{ ...tableShellSx, ...sx }}>
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mb: children ? 2 : 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: AH_TEXT }}>
              {title}
            </Typography>
          ) : null}
          {subtitle ? (
            <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>{subtitle}</Typography>
          ) : null}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const frameworkGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 1.25,
  alignItems: "stretch",
};

const FrameworkCards = ({ frameworks, selectedIds, onToggle }) => (
  <Box sx={frameworkGridSx}>
    {frameworks.map((fw) => {
      const addonId = asInteger(fw.complianceAddonId);
      const selected = addonId != null && selectedIds.includes(addonId);
      const label = frameworkLabel(fw);
      const icon = getComplianceIcon(label);
      const manualCount = fw.manualControlCount;
      return (
        <Box
          key={fw.complianceAddonId}
          component="button"
          type="button"
          disabled={addonId == null}
          onClick={() => onToggle(addonId)}
          sx={{
            ...cardShellSx,
            minHeight: 96,
            p: 1.5,
            textAlign: "left",
            cursor: "pointer",
            border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
            backgroundColor: selected ? "#fff4eb" : AH_WHITE,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            fontFamily: "inherit",
            "&:hover": { borderColor: selected ? AH_ACCENT : "#cbd5e1" },
          }}
        >
          <Box sx={{ width: 28, height: 28, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {icon ? (
              <Box component="img" src={icon} alt="" sx={{ maxHeight: 24, maxWidth: 28 }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: 20, color: AH_MUTED }} />
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={label} placement="top">
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: AH_TEXT, lineHeight: 1.35, ...clampSx(2) }}>
                {label}
              </Typography>
            </Tooltip>
            <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.5, lineHeight: 1.4, ...clampSx(2) }}>
              {Number.isInteger(manualCount)
                ? `${manualCount} manual control${manualCount === 1 ? "" : "s"}`
                : "—"}
            </Typography>
          </Box>
          {selected ? <CheckCircleOutlineIcon sx={{ fontSize: 18, color: AH_ACCENT, flexShrink: 0 }} /> : null}
        </Box>
      );
    })}
  </Box>
);

const CreateAudit = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedProvider } = useContext(CloudProviderContext);
  const accounts = useAccounts();
  const organization = useOrganization();
  const { users, usersLoading } = useUsers();
  const createState = useSelector((s) => s.auditHub?.create) || {};
  const [form, setForm] = useState(readDraft);
  const [errors, setErrors] = useState({});
  const [accountQuery, setAccountQuery] = useState("");
  const [catalogByAccount, setCatalogByAccount] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const skipFirstSave = useRef(true);
  const catalogRef = useRef({});

  useEffect(() => {
    document.title = "Create New Audit";
    dispatch(resetCreateAudit());
  }, [dispatch]);

  useEffect(() => {
    if (skipFirstSave.current) {
      skipFirstSave.current = false;
      return undefined;
    }
    const handle = setTimeout(() => {
      writeDraft(form);
    }, 700);
    return () => clearTimeout(handle);
  }, [form]);

  useEffect(() => {
    if (createState.status === "succeeded") {
      const failures = Array.isArray(createState.audit?.scanDispatchFailures)
        ? createState.audit.scanDispatchFailures.filter(Boolean)
        : [];
      clearDraft();
      dispatch(fetchAudits({ page: 1, size: 100 }));
      dispatch(resetCreateAudit());
      history.push({
        pathname: "/audit-hub",
        state: failures.length ? { scanDispatchFailures: failures } : undefined,
      });
    } else if (createState.status === "failed" && createState.error) {
      setSnackbar({ open: true, message: createState.error, severity: "error" });
    }
  }, [createState.status, createState.error, dispatch, history]);

  const userOptions = useMemo(
    () =>
      excludeMspUsers(users, organization)
        .filter((user) => user.email)
        .map((user) => ({
          email: user.email,
          label: formatUserLabel(user),
        })),
    [users, organization]
  );

  const providerAccounts = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    return accounts.filter((account) => {
      if (account.accountNumber?.includes("INIT00000001")) return false;
      if (account.accountNumber?.includes("TEST")) return false;
      return getAccountCloudType(account) === selectedProvider;
    });
  }, [accounts, selectedProvider]);

  const selectedAccountKey = form.accounts.map((entry) => entry.accountNumber).join(",");

  useEffect(() => {
    if (usersLoading || !Array.isArray(users)) return undefined;
    const emails = new Set(userOptions.map((option) => option.email));
    setForm((prev) => {
      const ownerEmail = emails.has(prev.ownerEmail) ? prev.ownerEmail : "";
      const defaultAssigneeEmail = emails.has(prev.defaultAssigneeEmail) ? prev.defaultAssigneeEmail : "";
      if (ownerEmail === prev.ownerEmail && defaultAssigneeEmail === prev.defaultAssigneeEmail) return prev;
      return { ...prev, ownerEmail, defaultAssigneeEmail };
    });
    return undefined;
  }, [userOptions, users, usersLoading]);

  useEffect(() => {
    selectedAccountKey.split(",").filter(Boolean).forEach((accountNumber) => {
      if (!AWS_ACCOUNT_NUMBER.test(accountNumber)) return;
      const current = catalogRef.current[accountNumber];
      if (current?.status === "loading" || current?.status === "succeeded") return;
      catalogRef.current[accountNumber] = { status: "loading", items: [], error: null };
      setCatalogByAccount({ ...catalogRef.current });
      listAllCatalogFrameworks({ accountNumber })
        .then((items) => {
          catalogRef.current[accountNumber] = { status: "succeeded", items, error: null };
          setCatalogByAccount({ ...catalogRef.current });
        })
        .catch((error) => {
          catalogRef.current[accountNumber] = {
            status: "failed",
            items: [],
            error: error?.message || "Unable to load frameworks.",
          };
          setCatalogByAccount({ ...catalogRef.current });
        });
    });
  }, [selectedAccountKey]);

  const visibleAccounts = useMemo(() => {
    const q = accountQuery.trim().toLowerCase();
    if (!q) return providerAccounts;
    return providerAccounts.filter((account) =>
      `${account.accountName || ""}`.toLowerCase().includes(q)
    );
  }, [providerAccounts, accountQuery]);

  useEffect(() => {
    if (!providerAccounts.length) return;
    const allowed = new Set(providerAccounts.map((account) => account.accountNumber));
    setForm((prev) => {
      const accountsNext = prev.accounts.filter((entry) => allowed.has(entry.accountNumber));
      if (accountsNext.length === prev.accounts.length) return prev;
      return { ...prev, accounts: accountsNext };
    });
  }, [providerAccounts]);

  useEffect(() => {
    setForm((prev) => {
      let changed = false;
      const accountsNext = prev.accounts.map((entry) => {
        const catalog = catalogByAccount[entry.accountNumber];
        if (!catalog || catalog.status !== "succeeded") return entry;
        const allowed = new Set(
          (catalog.items || []).map((fw) => asInteger(fw.complianceAddonId)).filter((id) => id != null)
        );
        const complianceAddonIds = entry.complianceAddonIds.filter((id) => allowed.has(id));
        if (complianceAddonIds.length === entry.complianceAddonIds.length) return entry;
        changed = true;
        return { ...entry, complianceAddonIds };
      });
      return changed ? { ...prev, accounts: accountsNext } : prev;
    });
  }, [catalogByAccount]);

  const patch = (partial) => setForm((prev) => ({ ...prev, ...partial }));

  const updateAccount = (accountNumber, partial) => {
    setForm((prev) => ({
      ...prev,
      accounts: prev.accounts.map((entry) =>
        entry.accountNumber === accountNumber ? { ...entry, ...partial } : entry
      ),
    }));
  };

  const toggleAccount = (account) => {
    setForm((prev) => {
      const exists = prev.accounts.some((entry) => entry.accountNumber === account.accountNumber);
      if (exists) {
        return {
          ...prev,
          accounts: prev.accounts.filter((entry) => entry.accountNumber !== account.accountNumber),
        };
      }
      if (prev.accounts.length >= MAX_AUDIT_ACCOUNTS) return prev;
      const regions = Array.isArray(account.regions)
        ? account.regions
            .filter((region) => typeof region === "string" && region.trim())
            .slice(0, MAX_ACCOUNT_REGIONS)
        : [];
      return { ...prev, accounts: [...prev.accounts, emptyAccount(account.accountNumber, regions)] };
    });
    setErrors((prev) => ({ ...prev, accounts: undefined, scopes: undefined }));
  };

  const toggleFramework = (accountNumber, addonId) => {
    setForm((prev) => ({
      ...prev,
      accounts: prev.accounts.map((entry) => {
        if (entry.accountNumber !== accountNumber || addonId == null) return entry;
        const has = entry.complianceAddonIds.includes(addonId);
        if (!has && entry.complianceAddonIds.length >= MAX_ACCOUNT_FRAMEWORKS) return entry;
        return {
          ...entry,
          complianceAddonIds: has
            ? entry.complianceAddonIds.filter((id) => id !== addonId)
            : [...entry.complianceAddonIds, addonId],
        };
      }),
    }));
    setErrors((prev) => ({ ...prev, scopes: undefined }));
  };

  const setAccountRegions = (accountNumber, value) => {
    const regions = [...new Set((value || []).map((region) => String(region).trim()).filter(Boolean))].slice(
      0,
      MAX_ACCOUNT_REGIONS
    );
    updateAccount(accountNumber, { regions });
  };

  const frameworksLoading = form.accounts.some(
    (entry) => catalogByAccount[entry.accountNumber]?.status === "loading"
  );

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Audit name is required.";
    if (!form.ownerEmail) next.ownerEmail = "Audit owner is required.";
    if (!form.accounts.length) next.accounts = "Select at least one account.";
    else if (form.accounts.length > MAX_AUDIT_ACCOUNTS) {
      next.accounts = `An audit can include at most ${MAX_AUDIT_ACCOUNTS} accounts.`;
    } else if (form.accounts.some((entry) => !AWS_ACCOUNT_NUMBER.test(entry.accountNumber || ""))) {
      next.accounts = "Each account number must be 12 digits.";
    }
    const scopeProblems = [];
    form.accounts.forEach((entry) => {
      const catalog = catalogByAccount[entry.accountNumber];
      const label =
        providerAccounts.find((account) => account.accountNumber === entry.accountNumber)?.accountName ||
        entry.accountNumber;
      if (!entry.complianceAddonIds.length) {
        scopeProblems.push(`${label} needs at least one framework.`);
        return;
      }
      if (entry.complianceAddonIds.length > MAX_ACCOUNT_FRAMEWORKS) {
        scopeProblems.push(`${label} can include at most ${MAX_ACCOUNT_FRAMEWORKS} frameworks.`);
      }
      if (entry.regions.length > MAX_ACCOUNT_REGIONS) {
        scopeProblems.push(`${label} can include at most ${MAX_ACCOUNT_REGIONS} regions.`);
      }
      if (catalog?.status === "failed") {
        scopeProblems.push(catalog.error || `Unable to load frameworks for ${label}.`);
        return;
      }
      if (catalog?.status === "succeeded") {
        const allowed = new Set(
          (catalog.items || []).map((fw) => asInteger(fw.complianceAddonId)).filter((id) => id != null)
        );
        if (entry.complianceAddonIds.some((id) => !allowed.has(id))) {
          scopeProblems.push(`Choose frameworks that ${label} can be audited on.`);
        }
      }
    });
    if (scopeProblems.length) next.scopes = scopeProblems[0];
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleLaunch = () => {
    if (!validate()) {
      setSnackbar({
        open: true,
        message: "Complete the required fields before launching the audit.",
        severity: "error",
      });
      return;
    }
    const body = {
      name: form.name.trim(),
      ownerEmail: form.ownerEmail,
      accounts: form.accounts.map((entry) => {
        const account = { accountNumber: entry.accountNumber, complianceAddonIds: entry.complianceAddonIds };
        if (entry.regions.length) account.regions = entry.regions;
        return account;
      }),
    };
    if (form.defaultAssigneeEmail) body.defaultAssigneeEmail = form.defaultAssigneeEmail;
    if (form.dueDate) body.dueDate = form.dueDate;
    dispatch(createAudit(body));
  };

  const ownerValue = userOptions.find((u) => u.email === form.ownerEmail) || null;
  const assigneeValue = userOptions.find((u) => u.email === form.defaultAssigneeEmail) || null;
  const launching = createState.status === "loading";

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
          <Box
            sx={{
              pt: 1.5,
              pb: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              ...auditHubPageSx,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
              <Breadcrumbs sx={{ fontSize: 13, color: AH_MUTED }}>
                <Link
                  component="button"
                  underline="hover"
                  color="inherit"
                  onClick={() => history.push("/audit-hub")}
                  sx={{ fontSize: 13 }}
                >
                  Audits
                </Link>
                <Typography sx={{ fontSize: 13, color: AH_TEXT }}>Create New Audit</Typography>
              </Breadcrumbs>
            </Box>

            <Box sx={tableShellSx}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.125rem", md: "1.25rem" },
                  color: AH_TEXT,
                  letterSpacing: "-0.01em",
                }}
              >
                Create New Audit
              </Typography>
              <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>
                Add up to 50 accounts. Each account has its own regions and frameworks.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 5fr) minmax(0, 7fr)" },
                gridTemplateAreas: {
                  xs: `"timeline" "meta" "account" "scope"`,
                  lg: `"timeline account" "meta scope"`,
                },
                gap: 2,
                alignItems: "start",
              }}
            >
                <SectionCard
                  title="Metadata & Governance"
                  subtitle="Name, owner, and default assignee (applied to manual controls at launch)."
                  sx={{ gridArea: "meta", alignSelf: "stretch", minWidth: 0 }}
                >
                  <TextField
                    label="Audit Name"
                    required
                    fullWidth
                    value={form.name}
                    onChange={(e) => patch({ name: e.target.value })}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    placeholder="Q3 2026 Security Compliance Audit"
                    inputProps={{ maxLength: 200 }}
                    sx={{ ...fieldSx, mb: 2 }}
                  />
                  <Autocomplete
                    options={userOptions}
                    loading={usersLoading}
                    value={ownerValue}
                    onChange={(_e, option) => patch({ ownerEmail: option?.email || "" })}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(a, b) => a.email === b.email}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Audit Owner"
                        required
                        error={Boolean(errors.ownerEmail)}
                        helperText={
                          errors.ownerEmail ||
                          (!usersLoading && userOptions.length === 0
                            ? "No client-organization users are available. MSP users are not listed."
                            : undefined)
                        }
                        sx={{ ...fieldSx, mb: 2 }}
                      />
                    )}
                  />
                  <Autocomplete
                    options={userOptions}
                    loading={usersLoading}
                    value={assigneeValue}
                    onChange={(_e, option) => patch({ defaultAssigneeEmail: option?.email || "" })}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(a, b) => a.email === b.email}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Default Assignee"
                        helperText={
                          !usersLoading && userOptions.length === 0
                            ? "No client-organization users are available. MSP users are not listed."
                            : "Optional. Applied to every manual control at launch. MSP users are not listed."
                        }
                        sx={fieldSx}
                      />
                    )}
                  />
                </SectionCard>

                <SectionCard
                  title="Timeline"
                  subtitle="Optional target completion date. Start time is set when the audit is launched."
                  sx={{ gridArea: "timeline", alignSelf: "stretch", minWidth: 0 }}
                >
                  <DueDateField value={form.dueDate} onChange={(dueDate) => patch({ dueDate })} />
                </SectionCard>
                <SectionCard
                  title="Regions & Frameworks"
                  subtitle="Chosen per account. A framework selected for one account is not added to the others."
                  sx={{ gridArea: "scope", alignSelf: "stretch", minWidth: 0 }}
                  action={
                    <Typography sx={{ fontSize: 12, color: errors.scopes ? "#dc2626" : AH_MUTED, whiteSpace: "nowrap", pt: 0.25 }}>
                      {form.accounts.reduce((count, entry) => count + entry.complianceAddonIds.length, 0)} selected
                    </Typography>
                  }
                >
                  {!form.accounts.length ? (
                    <Typography sx={{ fontSize: 13, color: AH_MUTED }}>
                      Select one or more accounts to choose regions and frameworks.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 560, overflowY: "auto", pr: 0.5 }}>
                      {form.accounts.map((entry) => {
                        const account = providerAccounts.find((item) => item.accountNumber === entry.accountNumber);
                        const catalog = catalogByAccount[entry.accountNumber] || { status: "idle", items: [], error: null };
                        const regionOptions = selectedProvider === "AWS" ? AWS_REGION_CODES : [];
                        return (
                          <Box key={entry.accountNumber} sx={{ ...cardShellSx, p: 1.5, backgroundColor: AH_WHITE }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: AH_TEXT }}>
                              {account?.accountName || entry.accountNumber}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: AH_MUTED, mb: 1.25 }}>{entry.accountNumber}</Typography>
                            <Autocomplete
                              multiple
                              size="small"
                              options={regionOptions}
                              freeSolo={selectedProvider !== "AWS"}
                              value={entry.regions}
                              onChange={(_event, value) => setAccountRegions(entry.accountNumber, value)}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                  const { key, ...tagProps } = getTagProps({ index });
                                  return <Chip key={key} {...tagProps} label={option} size="small" />;
                                })
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Regions"
                                  placeholder={entry.regions.length ? "" : "Optional"}
                                  helperText={`Optional. Up to ${MAX_ACCOUNT_REGIONS} region codes for this account's scan. ${entry.regions.length} selected.`}
                                  sx={{ ...fieldSx, mb: 1.5 }}
                                />
                              )}
                            />
                            <Typography sx={{ fontSize: 12, color: AH_MUTED, mb: 1 }}>
                              {entry.complianceAddonIds.length} of {MAX_ACCOUNT_FRAMEWORKS} frameworks
                            </Typography>
                            {catalog.status === "loading" || catalog.status === "idle" ? (
                              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress size={22} />
                              </Box>
                            ) : catalog.status === "failed" ? (
                              <Typography sx={{ fontSize: 13, color: "#dc2626" }}>{catalog.error}</Typography>
                            ) : catalog.items.length === 0 ? (
                              <Typography sx={{ fontSize: 13, color: AH_MUTED }}>
                                No frameworks are available for this account.
                              </Typography>
                            ) : (
                              <FrameworkCards
                                frameworks={catalog.items}
                                selectedIds={entry.complianceAddonIds}
                                onToggle={(addonId) => toggleFramework(entry.accountNumber, addonId)}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {errors.scopes ? (
                    <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 1.25 }}>{errors.scopes}</Typography>
                  ) : null}
                </SectionCard>

                <SectionCard
                  title={selectedProvider === "AZURE" ? "Azure Subscriptions" : "Cloud Accounts"}
                  subtitle={`Up to ${MAX_AUDIT_ACCOUNTS} accounts. Each one starts its own scan.`}
                  sx={{ gridArea: "account", alignSelf: "stretch", minWidth: 0 }}
                  action={
                    <Typography sx={{ fontSize: 12, color: errors.accounts ? "#dc2626" : AH_MUTED, whiteSpace: "nowrap", pt: 0.25 }}>
                      {form.accounts.length} of {MAX_AUDIT_ACCOUNTS}
                    </Typography>
                  }
                >
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={selectedProvider === "AZURE" ? "Search subscriptions..." : "Search accounts..."}
                    value={accountQuery}
                    onChange={(e) => setAccountQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ ...searchFieldSx, mb: 1.25 }}
                  />
                  <Box
                    sx={{
                      ...cardShellSx,
                      maxHeight: 220,
                      overflowY: "auto",
                      borderColor: errors.accounts ? "#dc2626" : AH_BORDER,
                    }}
                  >
                    {visibleAccounts.length === 0 ? (
                      <Typography sx={{ p: 2, fontSize: 13, color: AH_MUTED }}>
                        No {selectedProvider === "AZURE" ? "subscriptions" : "accounts"} match this search.
                      </Typography>
                    ) : (
                      visibleAccounts.map((account) => {
                        const selected = form.accounts.some((entry) => entry.accountNumber === account.accountNumber);
                        const eligible = AWS_ACCOUNT_NUMBER.test(account.accountNumber || "");
                        const atLimit = !selected && form.accounts.length >= MAX_AUDIT_ACCOUNTS;
                        const unavailable = atLimit || !eligible;
                        return (
                          <Box
                            key={account.accountCode || account.accountNumber}
                            role="checkbox"
                            aria-checked={selected}
                            aria-disabled={unavailable}
                            tabIndex={unavailable ? -1 : 0}
                            onClick={() => {
                              if (!unavailable) toggleAccount(account);
                            }}
                            onKeyDown={(event) => {
                              if (unavailable) return;
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                toggleAccount(account);
                              }
                            }}
                            sx={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1.5,
                              minHeight: 44,
                              border: 0,
                              borderBottom: `1px solid ${AH_BORDER}`,
                              backgroundColor: selected ? "#fff4eb" : AH_WHITE,
                              cursor: unavailable ? "not-allowed" : "pointer",
                              opacity: unavailable ? 0.45 : 1,
                              fontFamily: "inherit",
                              textAlign: "left",
                              "&:last-of-type": { borderBottom: 0 },
                              "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={selected}
                              tabIndex={-1}
                              disableRipple
                              sx={{ p: 0, pointerEvents: "none", color: AH_BORDER, "&.Mui-checked": { color: AH_ACCENT } }}
                            />
                            <CloudProviderMark
                              provider={selectedProvider}
                              sx={{
                                mr: 0,
                                "& .cloud-provider-mark--aws": { height: 14, width: 26 },
                                "& .cloud-provider-mark--azure": { height: 16, width: 16 },
                              }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography noWrap sx={{ fontSize: 13, fontWeight: selected ? 700 : 500, color: AH_TEXT }}>
                                {account.accountName || account.accountCode}
                              </Typography>
                              <Typography noWrap sx={{ fontSize: 12, color: AH_MUTED }}>
                                {eligible ? account.accountNumber : `${account.accountNumber || "—"} · 12-digit account required`}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                  {errors.accounts ? (
                    <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 1.25 }}>{errors.accounts}</Typography>
                  ) : null}
                </SectionCard>
            </Box>

            <Box
              sx={{
                ...tableShellSx,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                position: { md: "sticky" },
                bottom: 0,
                zIndex: 2,
              }}
            >
              <Button variant="outlined" onClick={() => history.push("/audit-hub")} sx={outlinedButtonSx}>
                Cancel
              </Button>
              <Button
                startIcon={<RocketLaunchOutlinedIcon />}
                onClick={handleLaunch}
                disabled={launching || frameworksLoading}
                sx={primaryButtonSx}
              >
                {launching ? "Launching..." : "Launch Audit"}
              </Button>
            </Box>
          </Box>
        </Container>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AuditHubThemeProvider>
  );
};

export default CreateAudit;
